<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

import SupportModal from "@/components/shared/SupportModal.vue";
import SidebarHeader from "@/components/sidebar/SidebarHeader.vue";
import SidebarUserFooter from "@/components/sidebar/SidebarUserFooter.vue";
import SnsButton from "@/components/ui/SnsButton.vue";
import SidebarMenuLayout from "@/layouts/SidebarMenuLayout";

const store = useStore();
const route = useRoute();

const isSupportModalOpen = ref(false);
const navigation = ref(null);
let resizeObserver = null;

const user = computed(() => store.getters.getUser);
const collapsed = computed(() => store.getters.getSidebarCollapsed);
const isOnPrem = computed(() => store.getters.isOnPrem);

const navigationOpen = computed({
  get: () => store.getters.getNavigationOpen,
  set: (value) => store.dispatch("setNavigationOpen", value),
});

const toggleNavigationOpen = () => {
  navigationOpen.value = !navigationOpen.value;
};

const onNavClick = (e) => {
  if (collapsed.value && !e.target.closest("a, button")) {
    store.dispatch("toggleSidebarCollapsed");
  }
};

watch(navigationOpen, (value) => {
  if (value) {
    document.body.classList.add("modal-open");
  } else {
    document.body.classList.remove("modal-open");
  }
});

watch(
  () => route.path,
  () => store.dispatch("setNavigationOpen", false),
);

onMounted(() => {
  if (navigation.value) {
    resizeObserver = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      document.documentElement.style.setProperty("--sidebar-width", `${width}px`);
    });
    resizeObserver.observe(navigation.value);
  }
});

onBeforeUnmount(() => {
  document.body.classList.remove("modal-open");
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
});
</script>

<template>
  <div class="sidebar">
    <nav
      ref="navigation"
      class="navigation"
      :class="{ open: navigationOpen, collapsed: collapsed }"
      @click="onNavClick"
    >
      <div class="navigation-inner">
        <SidebarHeader
          :collapsed="collapsed"
          @toggle-collapse="store.dispatch('toggleSidebarCollapsed')"
          @close-mobile="toggleNavigationOpen"
        />

        <div class="navigation-menu">
          <SidebarMenuLayout v-if="user && user.id" :collapsed="collapsed" />
        </div>

        <div v-if="!isOnPrem" class="navigation-help">
          <SnsButton
            class="btn-warning btn-outline btn-block btn-sm"
            :class="{ 'px-0': collapsed }"
            :title="!collapsed ? $t('common.label.helpAndSupport') : ''"
            @click="isSupportModalOpen = true"
          >
            <i class="bi bi-question-circle-fill text-base" />
            <span v-if="!collapsed" class="ml-2">{{ $t("common.label.helpAndSupport") }}</span>
          </SnsButton>
        </div>

        <SidebarUserFooter :collapsed="collapsed" />
      </div>
    </nav>

    <div v-if="navigationOpen" class="navigation-overlay" @click="toggleNavigationOpen" />

    <SupportModal :is-modal-on="isSupportModalOpen" @close="isSupportModalOpen = false" />
  </div>
</template>

<style lang="scss" scoped>
.navigation {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 10;
  width: fit-content;
  min-width: $sidebar-min-width;
  max-width: $sidebar-max-width;
  transition:
    min-width 0.2s ease,
    transform 0.2s ease;
  @apply border-r border-r-accent;

  &.collapsed {
    width: $sidebar-collapsed-width;
    min-width: $sidebar-collapsed-width;
    cursor: e-resize;

    a,
    button {
      cursor: pointer;
    }
  }

  &.open {
    transform: translateX(0);
    z-index: 11;
  }

  @media screen and (max-width: 767px) {
    transform: translateX(-100%);
    max-width: $sidebar-max-width;

    &.collapsed {
      width: 70%;
      min-width: auto;
    }
  }

  .navigation-inner {
    display: flex;
    flex-direction: column;
    background-color: oklch(var(--b1));
    position: relative;
    z-index: 10;
    height: 100%;

    .navigation-menu {
      flex: 1;
      overflow-y: auto;
      min-height: 0;
    }

    .navigation-help {
      flex-shrink: 0;
      padding: 0.5rem 0.75rem 0.25rem;
    }
  }
}

.navigation-overlay {
  background-color: oklch(var(--bc));
  opacity: 0.7;
  position: fixed;
  z-index: 10;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  @media screen and (min-width: $screen-size-tablet) {
    display: none;
  }
}
</style>
